import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Users, ClipboardList, Briefcase, FileText, Plus, MapPin, User, Settings, LogOut, Menu, X } from 'lucide-react';
import { locationsAPI, showConfirmModal } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [showOperationsMenu, setShowOperationsMenu] = useState(false);
  const [showProjectsMenu, setShowProjectsMenu] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showLocationsDropdown, setShowLocationsDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const [mobileOperationsOpen, setMobileOperationsOpen] = useState(false);
  const [mobileCreateOpen, setMobileCreateOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const locationsRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (locationsRef.current && !locationsRef.current.contains(e.target)) {
        setShowLocationsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (showLocationsDropdown && locations.length === 0) {
      locationsAPI.getAll().then((data) => setLocations(data || [])).catch(() => {});
    }
  }, [showLocationsDropdown]);

  const toggleLocation = async (id) => {
    const loc = locations.find((l) => l.location_id === id);
    if (!loc) return;
    try {
      await locationsAPI.update(id, { checked: !loc.checked });
      setLocations(locations.map((l) => l.location_id === id ? { ...l, checked: !l.checked } : l));
    } catch { toast.error('Failed to update location'); }
  };

  const toggleAll = async () => {
    const allChecked = locations.every((l) => l.checked);
    try {
      await Promise.all(locations.map((l) => locationsAPI.update(l.location_id, { checked: !allChecked })));
      setLocations(locations.map((l) => ({ ...l, checked: !allChecked })));
    } catch { toast.error('Failed to update locations'); }
  };

  const handleConfirmLocations = async () => {
    try {
      await locationsAPI.confirmSelection();
      toast.success('Location selection confirmed');
      setShowLocationsDropdown(false);
    } catch { toast.error('Failed to confirm selection'); }
  };

  const isActive = (path) => location.pathname === path;
  const userName = (() => { try { const u = JSON.parse(localStorage.getItem('user')); return u?.name || 'User'; } catch { return 'User'; } })();

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-[#FFA94D] shadow-md">
        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center justify-between">
          {/* Logo */}
          <Link to="/projects" className="flex items-center px-6 py-4 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 text-2xl font-light">resto</span>
              <div className="w-10 h-10 rounded-full bg-[#FFA94D] flex items-center justify-center">
                <span className="text-white text-sm font-semibold">mp</span>
              </div>
            </div>
          </Link>

          {/* Main Navigation */}
          <div className="flex items-center h-full">
            <Link
              to="/customers"
              className={`px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors ${isActive('/customers') ? 'bg-[#E07514]' : ''}`}
              data-testid="nav-customers"
            >
              <span className="text-lg">Customers</span>
            </Link>

            {/* Projects Dropdown */}
            <div className="relative" onMouseEnter={() => setShowProjectsMenu(true)} onMouseLeave={() => setShowProjectsMenu(false)}>
              <button className={`px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors flex items-center gap-1 ${isActive('/projects') || isActive('/projects/accounts-receivable') ? 'bg-[#E07514]' : ''}`} data-testid="nav-projects">
                <span className="text-lg">Projects</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showProjectsMenu && (
                <div className="absolute top-full left-0 bg-white shadow-lg rounded-b-md min-w-[280px] z-50">
                  <Link to="/projects" className="px-6 py-4 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b"><ClipboardList className="w-5 h-5" /><span className="text-lg">In Progress</span></Link>
                  <Link to="/projects/accounts-receivable" className="px-6 py-4 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b"><ClipboardList className="w-5 h-5" /><span className="text-lg">Accounts Receivable</span></Link>
                  <Link to="/projects/closed" className="px-6 py-4 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b"><ClipboardList className="w-5 h-5" /><span className="text-lg">Closed</span></Link>
                  <Link to="/projects/dead" className="px-6 py-4 hover:bg-gray-100 flex items-center gap-3 text-gray-700"><ClipboardList className="w-5 h-5" /><span className="text-lg">Dead</span></Link>
                </div>
              )}
            </div>

            {/* Operations Dropdown */}
            <div className="relative" onMouseEnter={() => setShowOperationsMenu(true)} onMouseLeave={() => setShowOperationsMenu(false)}>
              <button className={`px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors flex items-center gap-1 ${isActive('/withdrawals') ? 'bg-[#E07514]' : ''}`} data-testid="nav-operations">
                <span className="text-lg">Operations</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showOperationsMenu && (
                <div className="absolute top-full left-0 bg-white shadow-lg rounded-b-md min-w-[320px] z-50">
                  <div className="px-6 py-3 bg-gray-100 text-gray-400 text-sm uppercase tracking-wide">Labor</div>
                  <Link to="/timeclock" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-employee-time"><Briefcase className="w-5 h-5" /><span>Employee Time</span></Link>
                  <Link to="/print-time-cards" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-time-review"><ClipboardList className="w-5 h-5" /><span>Time Review</span></Link>
                  <Link to="/project-costs" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-project-costs"><span className="text-xl ml-1">$</span><span>Project Costs</span></Link>
                  {hasPermission('commission_management') && (
                  <>
                  <div className="px-6 py-3 bg-gray-100 text-gray-400 text-sm uppercase tracking-wide">Commissions & Bonuses</div>
                  <Link to="/management-bonus" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-mgmt-commission"><span className="text-xl ml-1">&#8373;</span><span>Mgmt Commission</span></Link>
                  <Link to="/commission-overview" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-commission-overview"><span className="text-xl ml-1">&#8373;</span><span>Overview</span></Link>
                  <Link to="/pm-breakdown" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-pm-breakdown"><Users className="w-5 h-5" /><span>PM Breakdown</span></Link>
                  <Link to="/paid-commissions" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-paid-commissions"><span className="text-xl ml-1">&#10003;</span><span>Paid Commissions</span></Link>
                  <Link to="/commissions-alerts" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-commissions-alerts"><span className="text-xl ml-1">&#9888;</span><span>Commissions Alerts</span></Link>
                  <Link to="/withdrawals" className="px-6 py-3 hover:bg-gray-100 flex items-center gap-3 text-gray-700" data-testid="nav-withdrawals"><span className="text-xl ml-1">&#8644;</span><span>Withdrawals</span></Link>
                  </>
                  )}
                </div>
              )}
            </div>

            <Link to="/reports" className="px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors" data-testid="nav-reports"><span className="text-lg">Reports</span></Link>
          </div>

          {/* Right Side Menu */}
          <div className="flex items-center">
            {hasPermission('can_create') && (
            <div className="relative" onMouseEnter={() => setShowCreateMenu(true)} onMouseLeave={() => setShowCreateMenu(false)}>
              <button className="px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors flex items-center gap-1" data-testid="nav-create">
                <span className="text-lg">Create</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showCreateMenu && (
                <div className="absolute top-full right-0 bg-white shadow-lg rounded-b-md min-w-[200px] z-50">
                  <Link to="/customers/new" className="px-6 py-4 hover:bg-gray-100 flex items-center gap-3 text-gray-700 border-b" data-testid="nav-create-customer"><User className="w-5 h-5" /><span className="text-lg">Customer</span></Link>
                  <Link to="/projects/new" className="px-6 py-4 hover:bg-gray-100 flex items-center gap-3 text-gray-700" data-testid="nav-create-project"><ClipboardList className="w-5 h-5" /><span className="text-lg">Project</span></Link>
                </div>
              )}
            </div>
            )}

            <div className="relative" ref={locationsRef}>
              <button
                onClick={() => setShowLocationsDropdown(!showLocationsDropdown)}
                className="px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors flex items-center gap-1"
                data-testid="locations-dropdown-btn"
              >
                <span className="text-lg">All Locations</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showLocationsDropdown && (
                <div className="absolute top-full right-0 bg-white shadow-lg rounded-b-md min-w-[320px] z-50" data-testid="locations-dropdown">
                  <div className="p-4 space-y-1">
                    {locations.map((loc) => (
                      <label
                        key={loc.location_id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 px-3 py-2 rounded transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={loc.checked}
                          onChange={() => toggleLocation(loc.location_id)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <Users className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700 text-sm flex-1">
                          {loc.name}{' '}
                          <span className="text-gray-400">[{loc.zip_code}]</span>
                        </span>
                      </label>
                    ))}
                    <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 px-3 py-2 rounded transition-colors border-t mt-2 pt-3">
                      <input
                        type="checkbox"
                        checked={locations.length > 0 && locations.every((l) => l.checked)}
                        onChange={toggleAll}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700 text-sm font-semibold">All Locations</span>
                    </label>
                  </div>
                  <div className="px-4 pb-4 flex justify-center">
                    <button
                      onClick={handleConfirmLocations}
                      className="bg-[#DC3545] hover:bg-[#C82333] text-white px-6 py-2 rounded text-sm font-medium transition-colors"
                      data-testid="confirm-locations-btn"
                    >
                      Confirm Selection
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative" onMouseEnter={() => setShowUserMenu(true)} onMouseLeave={() => setShowUserMenu(false)}>
              <button className="px-6 py-5 text-white hover:bg-[#F08A2A] transition-colors flex items-center gap-1" data-testid="nav-user-menu">
                <span className="text-lg" suppressHydrationWarning>{userName}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showUserMenu && (
                <div className="absolute top-full right-0 bg-white shadow-lg rounded-b-md min-w-[200px] z-50">
                  {hasPermission('setup') && (
                  <Link to="/setup" className="px-6 py-4 hover:bg-gray-100 text-gray-700 border-b flex items-center gap-3" data-testid="menu-setup"><Settings className="w-4 h-4" />Setup</Link>
                  )}
                  <button onClick={async () => { if (await showConfirmModal('Are you sure you want to logout?', 'Logout')) { localStorage.removeItem('token'); navigate('/'); } }} className="px-6 py-4 hover:bg-gray-100 text-gray-700 block w-full text-left flex items-center gap-3" data-testid="menu-logout"><LogOut className="w-4 h-4" />Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3">
          <Link to="/projects" className="flex items-center gap-2">
            <span className="text-white text-xl font-light">resto</span>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
              <span className="text-[#FFA94D] text-xs font-semibold">mp</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-2" data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#F08A2A] border-t border-[#E07514]">
            <Link to="/customers" onClick={closeMobile} className="block px-6 py-3 text-white border-b border-[#E07514]">Customers</Link>

            {/* Projects */}
            <button onClick={() => setMobileProjectsOpen(!mobileProjectsOpen)} className="w-full px-6 py-3 text-white border-b border-[#E07514] flex justify-between items-center">
              Projects <ChevronDown className={`w-4 h-4 transition-transform ${mobileProjectsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileProjectsOpen && (
              <div className="bg-[#7AA525]">
                <Link to="/projects" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">In Progress</Link>
                <Link to="/projects/accounts-receivable" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Accounts Receivable</Link>
                <Link to="/projects/closed" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Closed</Link>
                <Link to="/projects/dead" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Dead</Link>
              </div>
            )}

            {/* Operations */}
            <button onClick={() => setMobileOperationsOpen(!mobileOperationsOpen)} className="w-full px-6 py-3 text-white border-b border-[#E07514] flex justify-between items-center">
              Operations <ChevronDown className={`w-4 h-4 transition-transform ${mobileOperationsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileOperationsOpen && (
              <div className="bg-[#7AA525]">
                <Link to="/timeclock" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Employee Time</Link>
                <Link to="/print-time-cards" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Time Review</Link>
                <Link to="/project-costs" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Project Costs</Link>
                {hasPermission('commission_management') && (
                <>
                <Link to="/management-bonus" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Mgmt Commission</Link>
                <Link to="/commission-overview" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Overview</Link>
                <Link to="/pm-breakdown" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">PM Breakdown</Link>
                <Link to="/paid-commissions" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Paid Commissions</Link>
                <Link to="/commissions-alerts" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Commissions Alerts</Link>
                <Link to="/withdrawals" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Withdrawals</Link>
                </>
                )}
              </div>
            )}

            <Link to="/reports" onClick={closeMobile} className="block px-6 py-3 text-white border-b border-[#E07514]">Reports</Link>

            {/* Create */}
            {hasPermission('can_create') && (
            <>
            <button onClick={() => setMobileCreateOpen(!mobileCreateOpen)} className="w-full px-6 py-3 text-white border-b border-[#E07514] flex justify-between items-center">
              Create <ChevronDown className={`w-4 h-4 transition-transform ${mobileCreateOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileCreateOpen && (
              <div className="bg-[#7AA525]">
                <Link to="/customers/new" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Customer</Link>
                <Link to="/projects/new" onClick={closeMobile} className="block px-10 py-3 text-white border-b border-[#E07514]">Project</Link>
              </div>
            )}
            </>
            )}

            <button onClick={() => { setShowLocationsDropdown(true); closeMobile(); }} className="w-full px-6 py-3 text-white border-b border-[#E07514] text-left">All Locations</button>
            {hasPermission('setup') && (
            <Link to="/setup" onClick={closeMobile} className="block px-6 py-3 text-white border-b border-[#E07514]">Setup</Link>
            )}
            <button onClick={async () => { if (await showConfirmModal('Are you sure you want to logout?', 'Logout')) { localStorage.removeItem('token'); navigate('/'); } }} className="w-full px-6 py-3 text-white text-left" suppressHydrationWarning>Logout ({userName})</button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
};

export default Layout;
